import { useMemo } from 'react';
import {
  useLocation,
  useParams,
  useSearchParams,
  type Params,
} from 'react-router';
import isServer from '~/scripts/isServer';
import parseQueryFromSearchParams from '~/scripts/parseQueryFromSearchParams';

export interface Route {
  path: string;
  hash: string;
  query: Record<string, unknown>;
  params: Readonly<Params<string>>;
  fullPath: string;
  name: string | null;
  meta: Record<string, unknown>;
  origin: string;
  href: string;
}

const isServerSide = isServer();

export default function useRouteDefault() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const params = useParams();

  const query = useMemo(
    () => parseQueryFromSearchParams(searchParams),
    [searchParams]
  );

  const route = useMemo((): Route => {
    const origin = isServerSide
      ? import.meta.env.VITE_ORIGIN || ''
      : window.location.origin;
    const href = isServerSide
      ? `${origin}${location.pathname}${location.search}${location.hash}`
      : window.location.href;

    return {
      path: location.pathname,
      hash: location.hash,
      query,
      params,
      fullPath: location.pathname + location.search + location.hash,
      name: null,
      meta: {},
      origin,
      href,
    };
  }, [location, params, query]);

  return route;
}
